import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import Nav from "@/components/Nav";
import {
  VideoCamera, ImageSquare, Sparkle, Clock, ArrowRight,
  DownloadSimple, ShareNetwork, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

const DURATIONS = [
  { value: 4,  label: "4 seconds", subtitle: "Fastest · cheapest" },
  { value: 8,  label: "8 seconds", subtitle: "Recommended" },
  { value: 12, label: "12 seconds", subtitle: "Full teaser" },
];

const SIZES = [
  { value: "1280x720", label: "16:9 Landscape", subtitle: "1280×720 · YouTube, web, LinkedIn" },
  { value: "720x1280", label: "9:16 Portrait",  subtitle: "720×1280 · Reels, Stories, Shorts" },
];

export default function BrandVideo() {
  const [brand, setBrand] = useState("");
  const [desc, setDesc] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [duration, setDuration] = useState(4);
  const [size, setSize] = useState("1280x720");
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);   // {status, video_url, error}
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    document.title = "Brand Teaser Video — Sora 2 | Talaio/AI";
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const onLogoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) {
      toast.error("Upload a PNG, JPEG or WebP logo.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Logo too large — under 5MB please.");
      return;
    }
    const r = new FileReader();
    r.onload = () => setLogoDataUrl(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (!brand.trim() || !desc.trim()) {
      toast.error("We need a brand name and a short description.");
      return;
    }
    setBusy(true);
    setStatus(null);
    setJobId(null);
    try {
      const body = {
        brand_name: brand.trim(),
        description: desc.trim(),
        duration,
        size,
      };
      if (logoDataUrl) {
        // Strip the data-URL prefix — backend expects raw base64
        body.logo_base64 = logoDataUrl.split(",")[1];
      }
      const { data } = await api.post("/brand-video/generate", body);
      setJobId(data.job_id);
      setStatus({ status: "queued" });
      toast.success(`Queued. ${data.remaining_today} free renders left today.`);
      // Poll status every 6s
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/brand-video/${data.job_id}`);
          setStatus(s);
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setBusy(false);
            if (s.status === "completed") toast.success("Your brand teaser is ready!");
            else toast.error(s.error || "Video generation failed.");
          }
        } catch (err) {
          // ignore transient poll failures
        }
      }, 6000);
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't queue the video. Please try again.";
      toast.error(msg);
      // Surface a persistent card so the right panel doesn't stay on the empty state
      const isLimit = err.response?.status === 429;
      setStatus({
        status: "failed",
        error: isLimit
          ? "You've used your free video renders for today. Sora 2 is expensive — sign up for the Startup Kit for more renders."
          : msg,
      });
      setJobId("blocked");
      setBusy(false);
    }
  };

  const share = async () => {
    if (!status?.video_url) return;
    const shareText = `Meet ${brand}. Our launch teaser — made in a click with Talaio/AI. ${status.video_url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${brand} — launch teaser`, text: shareText, url: status.video_url });
      } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success("Share text copied to clipboard.");
    }
  };

  const isDone = status?.status === "completed" && !!status?.video_url;
  const isFailed = status?.status === "failed";
  const isRunning = jobId && !isDone && !isFailed;

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pt-16 pb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-4">
          <Sparkle size={14} weight="fill"/> New — powered by Sora 2
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4 max-w-3xl">
          Your brand.<br/>
          <span className="text-[var(--pf-brand)]">In a cinematic reveal.</span>
        </h1>
        <p className="text-base md:text-lg text-[var(--pf-text-2)] max-w-2xl mb-8">
          Drop your logo, tell us what your brand stands for, hit generate. Sora 2 renders
          a 4–12 second launch teaser you can post to Instagram, LinkedIn, or WhatsApp Status.
        </p>
        <div className="flex flex-wrap gap-6 text-sm text-[var(--pf-text-2)]">
          <div className="flex items-center gap-2"><VideoCamera size={18} weight="duotone"/> 4, 8 or 12s durations</div>
          <div className="flex items-center gap-2"><Clock size={18} weight="duotone"/> Ready in 1–5 minutes</div>
          <div className="flex items-center gap-2"><ImageSquare size={18} weight="duotone"/> Logo watermark + endcard reveal</div>
          <div className="flex items-center gap-2"><ShareNetwork size={18} weight="duotone"/> Instant social share</div>
        </div>
      </section>

      {/* Studio */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8">
          {/* Left: form */}
          <div className="space-y-6">
            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)]">
                Brand
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] mb-1.5">
                  Brand name
                </label>
                <input
                  data-testid="bv-brand-name"
                  value={brand}
                  onChange={e => setBrand(e.target.value)}
                  placeholder="e.g. Brew Lab"
                  disabled={busy}
                  className="w-full px-3 py-2 rounded-md border border-[var(--pf-border)] bg-white text-sm focus:outline-none focus:border-[var(--pf-brand)]"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] mb-1.5">
                  Brand description
                </label>
                <textarea
                  data-testid="bv-desc"
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="e.g. Specialty coffee, sourced from single-origin Indian estates. Modern, warm, artisanal."
                  disabled={busy}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-[var(--pf-border)] bg-white text-sm focus:outline-none focus:border-[var(--pf-brand)] resize-none"
                />
                <div className="text-[10px] text-[var(--pf-text-3)] mt-1">
                  Sora uses this to set the mood. Be evocative.
                </div>
              </div>
            </div>

            {/* Logo */}
            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-3">
                Logo <span className="text-[var(--pf-text-3)] font-normal normal-case">· optional</span>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onLogoPick}
                className="hidden"
                data-testid="bv-logo-picker"
              />
              <div className="flex items-start gap-3">
                <div
                  className="w-20 h-20 border border-dashed border-[var(--pf-border)] rounded-lg flex items-center justify-center bg-[var(--pf-surface-2)]/40 shrink-0 overflow-hidden"
                  data-testid="bv-logo-preview"
                >
                  {logoDataUrl
                    ? <img src={logoDataUrl} alt="logo" className="max-w-full max-h-full object-contain"/>
                    : <ImageSquare size={26} className="text-[var(--pf-text-3)]"/>
                  }
                </div>
                <div className="flex-1">
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="pf-btn-secondary text-sm w-full mb-1.5"
                    data-testid="bv-logo-pick-btn"
                  >
                    {logoDataUrl ? "Change logo" : "Upload logo (PNG)"}
                  </button>
                  <div className="text-[10px] text-[var(--pf-text-3)] leading-tight">
                    Your logo is stitched into the final video — subtle watermark throughout + a full logo <b>reveal endcard</b> at the finish. Transparent PNG works best.
                    <Link to="/logo-studio" className="text-[var(--pf-brand)] font-bold ml-1">Don't have one? Free generator →</Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Duration + size */}
            <div className="border border-[var(--pf-border)] rounded-lg p-5 bg-white space-y-4">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)]">
                Format
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] mb-2">Duration</div>
                <div className="grid grid-cols-3 gap-2">
                  {DURATIONS.map(d => (
                    <button
                      key={d.value}
                      onClick={() => setDuration(d.value)}
                      disabled={busy}
                      data-testid={`bv-duration-${d.value}`}
                      className={`p-2.5 rounded border text-left transition-all ${
                        duration === d.value
                          ? "border-[var(--pf-brand)] bg-[var(--pf-brand)]/5"
                          : "border-[var(--pf-border)] hover:border-[var(--pf-text-2)]"
                      }`}
                    >
                      <div className="text-sm font-bold">{d.label}</div>
                      <div className="text-[10px] text-[var(--pf-text-2)]">{d.subtitle}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-text-2)] mb-2">Aspect ratio</div>
                <div className="grid grid-cols-1 gap-2">
                  {SIZES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setSize(s.value)}
                      disabled={busy}
                      data-testid={`bv-size-${s.value}`}
                      className={`p-2.5 rounded border text-left transition-all ${
                        size === s.value
                          ? "border-[var(--pf-brand)] bg-[var(--pf-brand)]/5"
                          : "border-[var(--pf-border)] hover:border-[var(--pf-text-2)]"
                      }`}
                    >
                      <div className="text-sm font-bold">{s.label}</div>
                      <div className="text-[10px] text-[var(--pf-text-2)]">{s.subtitle}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={submit}
              disabled={busy || !brand.trim() || !desc.trim()}
              data-testid="bv-generate"
              className="pf-btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy
                ? <>Rendering… <span className="pf-label ml-2">1–5 min</span></>
                : <><Sparkle size={16} weight="fill" className="inline mr-1"/> Generate my brand teaser</>
              }
            </button>
            <p className="text-[10px] text-[var(--pf-text-3)] text-center">
              Free · 2 renders per day · No account required · No watermark
            </p>
          </div>

          {/* Right: preview / status */}
          <div className="border border-[var(--pf-border)] rounded-lg bg-white p-8 min-h-[600px] flex flex-col items-center justify-center">
            {!jobId && (
              <div className="text-center max-w-md" data-testid="bv-empty">
                <div className="w-24 h-24 rounded-full bg-[var(--pf-surface-2)] flex items-center justify-center mx-auto mb-6">
                  <VideoCamera size={44} weight="duotone" className="text-[var(--pf-text-3)]"/>
                </div>
                <h3 className="text-xl font-black tracking-tight mb-2">Your teaser will appear here</h3>
                <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">
                  Fill out the form on the left and hit generate. Sora takes 1–5 minutes.
                  You can leave this tab open — we'll notify you when it's done.
                </p>
              </div>
            )}

            {isRunning && (
              <div className="text-center max-w-md" data-testid="bv-running">
                <div className="w-24 h-24 rounded-full bg-[var(--pf-brand)]/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <Clock size={44} weight="duotone" className="text-[var(--pf-brand)]"/>
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] mb-2">
                  {status?.status === "processing" ? "Rendering with Sora 2" : "Queued"}
                </div>
                <h3 className="text-xl font-black tracking-tight mb-2">Your brand teaser is on the way</h3>
                <p className="text-sm text-[var(--pf-text-2)] leading-relaxed mb-4">
                  This usually takes 1–5 minutes. We'll update this panel automatically when it's ready.
                </p>
                <div className="inline-block px-3 py-1 rounded-full bg-[var(--pf-surface-2)] text-[11px] font-mono text-[var(--pf-text-2)]">
                  {jobId}
                </div>
              </div>
            )}

            {isFailed && (
              <div className="text-center max-w-md" data-testid="bv-failed">
                <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                  <WarningCircle size={44} weight="duotone" className="text-red-500"/>
                </div>
                <h3 className="text-xl font-black tracking-tight mb-2">Render failed</h3>
                <p className="text-sm text-[var(--pf-text-2)] leading-relaxed mb-4">
                  {status?.error || "Sora couldn't finish this render. Please try again."}
                </p>
                <button
                  onClick={() => { setJobId(null); setStatus(null); }}
                  className="pf-btn-secondary text-sm"
                  data-testid="bv-retry"
                >
                  Try again
                </button>
              </div>
            )}

            {isDone && (
              <div className="w-full flex flex-col items-center" data-testid="bv-done">
                <div className="w-full max-w-2xl">
                  <video
                    src={status.video_url}
                    controls
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full rounded-lg shadow-lg bg-black"
                    data-testid="bv-video"
                  />
                </div>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <a
                    href={status.video_url}
                    download={`${brand}-teaser.mp4`}
                    data-testid="bv-download"
                    className="pf-btn-primary"
                  >
                    <DownloadSimple size={16} weight="bold" className="inline mr-1"/>
                    Download MP4
                  </a>
                  <button
                    onClick={share}
                    data-testid="bv-share"
                    className="pf-btn-secondary"
                  >
                    <ShareNetwork size={16} weight="bold" className="inline mr-1"/>
                    Share
                  </button>
                  <button
                    onClick={() => { setJobId(null); setStatus(null); }}
                    data-testid="bv-again"
                    className="text-sm font-bold px-4 py-2 hover:text-[var(--pf-brand)]"
                  >
                    Make another →
                  </button>
                </div>
                <div className="mt-6 border border-[var(--pf-border)] rounded-lg p-4 bg-[var(--pf-surface-2)]/40 max-w-md text-center">
                  <div className="text-xs pf-label mb-2">
                    <CheckCircle size={12} weight="fill" className="inline text-emerald-500 mr-1"/>
                    Post this to:
                  </div>
                  <div className="text-xs text-[var(--pf-text-2)] leading-relaxed">
                    Instagram Reels · LinkedIn · X · WhatsApp Status · YouTube Shorts
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upsell */}
        <div className="mt-12 border border-[var(--pf-border)] rounded-lg p-8 md:p-10 bg-gradient-to-br from-[var(--pf-brand)]/10 to-transparent">
          <div className="flex items-center gap-2 text-xs pf-label mb-3">
            <Sparkle size={14} weight="duotone"/> The full Startup-in-a-Box
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-3">
                Logo → Video → Print → Live.
              </h2>
              <p className="text-[var(--pf-text-2)] mb-6 leading-relaxed">
                Get a free AI logo. Turn it into a brand teaser. Print visiting cards,
                stationery and packaging designed to match. All under one roof.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/startup-kit" className="pf-btn-primary">Get the Startup Kit</Link>
                <Link to="/logo-studio" className="pf-btn-secondary">Free logo generator →</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { t: "1. Logo", d: "60 seconds · Free", to: "/logo-studio" },
                { t: "2. Video", d: "5 mins · Free", to: "/brand/video" },
                { t: "3. Print", d: "24h · Kit-included", to: "/products/visiting-cards" },
                { t: "4. Ship", d: "Pan-India · 5 days", to: "/startup-kit" },
              ].map((s, i) => (
                <Link key={i} to={s.to} className="p-4 rounded-lg bg-white border border-[var(--pf-border)] hover:border-[var(--pf-brand)] transition-colors group">
                  <div className="font-bold text-sm mb-1 group-hover:text-[var(--pf-brand)]">{s.t}</div>
                  <div className="text-[11px] text-[var(--pf-text-2)]">{s.d}</div>
                  <ArrowRight size={14} weight="bold" className="mt-2 text-[var(--pf-brand)] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"/>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
